import { createContext, useContext, useState, type ReactNode } from "react";

interface FilterOption {
  value: string;
  label: string;
}

interface FilterContextType {
  startDate: string;
  endDate: string;
  year: string;
  selectedFilter: string;
  isFilterOpen: boolean;
  isYearOpen: boolean;

  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setYear: (year: string) => void;
  setSelectedFilter: (filter: string) => void;
  setIsFilterOpen: (isOpen: boolean) => void;
  setIsYearOpen: (isOpen: boolean) => void;
  toggleFilterOpen: () => void;
  toggleYearOpen: () => void;

  handleApplyFilters: () => void;
  handleClearFilters: () => void;

  filterOptions: FilterOption[];
  years: string[];
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [year, setYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedFilter, setSelectedFilter] = useState<string>("");
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isYearOpen, setIsYearOpen] = useState<boolean>(false);

  const filterOptions: FilterOption[] = [
    { value: "total_sent", label: "Total Sent" },
    { value: "total_delivered", label: "Total Delivered" },
    { value: "total_pending", label: "Total Pending" },
    { value: "total_failed", label: "Total Failed" },
    { value: "total_absent_from_network", label: "Total Absent From Network" },
    { value: "invalid", label: "Invalid" },
    {
      value: "total_delivered_to_network",
      label: "Total Delivered To Network",
    },
    { value: "report_date", label: "Report Date" },
  ];

  const years = Array.from({ length: 5 }, (_, i) =>
    (new Date().getFullYear() - i).toString()
  );

  const toggleFilterOpen = () => setIsFilterOpen(!isFilterOpen);
  const toggleYearOpen = () => setIsYearOpen(!isYearOpen);

  const handleApplyFilters = () => {
    setIsFilterOpen(false);
    setIsYearOpen(false);
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setYear(new Date().getFullYear().toString());
    setSelectedFilter("");

    setIsFilterOpen(false);
    setIsYearOpen(false);
  };

  const value = {
    startDate,
    endDate,
    year,
    selectedFilter,
    isFilterOpen,
    isYearOpen,

    setStartDate,
    setEndDate,
    setYear,
    setSelectedFilter,
    setIsFilterOpen,
    setIsYearOpen,
    toggleFilterOpen,
    toggleYearOpen,

    // Helper functions
    handleApplyFilters,
    handleClearFilters,

    // Data
    filterOptions,
    years,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error("useFilters must be used within a FilterProvider");
  }
  return context;
}
